/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { createPreloadAction, createSetActiveItemAction } from '../../actions/header-actions';
import type { AudiHeaderSearchButtonProps } from '../../interfaces/header-components.interfaces';
import type { HeaderStyledSearchButtonProps } from '../../interfaces/header-styled-components.interfaces';
import { EventAction } from '../../utils/tracking';
import { ServiceContext } from '../../context/ServiceContext';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import { IconButton } from '@oneaudi/unified-web-components';

const HeaderStyledSearchButton = styled.div<HeaderStyledSearchButtonProps>`
  display: ${({ isOpen }): string => (isOpen ? 'block' : 'none')};
  margin: var(--one-header-space-s) 0;

  ${({ isSmallView, theme: { breakpoints } }) =>
    !isSmallView &&
    `@media screen and (min-width: ${breakpoints.l}px) {
      display: block;
      margin-right: var(--one-header-space-s);
    }`}

  margin-right: 0 !important;

  & button {
    background-color: transparent;
    cursor: pointer;
    display: flex;
    padding: 0;
  }

  & button:focus {
    outline: none;
  }

  & button:focus-visible {
    outline: auto;
  }

  & button svg {
    height: 24px;
    width: 24px;
  }
`;

const AudiHeaderSearchButtonAlpha: React.FC<AudiHeaderSearchButtonProps> = (props) => {
  const { headerStateService, trackingService } = useContext(ServiceContext);
  const { headerDispatch, headerNavigationItemsAmount, isOpen } = props;

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  const SearchAriaLabel = useI18n({
    defaultMessage: 'Search',
    id: 'nemo.ui.common.search-button',
  });

  useEffect(() => {
    const searchButton = document.querySelector('#search-button');
    searchButton?.classList.add(NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME);
  }, []);

  const handleSearchAccess: (event: React.MouseEvent<HTMLButtonElement>) => void = (event) => {
    event.preventDefault();
    // event tracking
    const SearchAccessEvent: TrackingDataV2 = {
      event: {
        eventInfo: {
          eventAction: EventAction.NAVIGATION,
          eventName: 'one header - search access',
        },
        attributes: {
          componentName: EventAction.COMPONENT_NAME,
          label: SearchAriaLabel,
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: `${headerNavigationItemsAmount + 1}`,
        },
      },
    };
    trackingService?.track(SearchAccessEvent);
    // dispatch status update
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: true,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
  };

  const handleFocusEvent: (
    event:
      | React.FocusEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => void = () => {
    // @ts-ignore
    headerDispatch(createPreloadAction());
  };

  return (
    <HeaderStyledSearchButton
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isOpen={isOpen}
    >
      <IconButton
        aria-label={SearchAriaLabel}
        icon="search"
        id="search-button"
        onClick={handleSearchAccess}
        // @ts-expect-error
        onMouseEnter={handleFocusEvent}
        onTouchStart={handleFocusEvent}
        size="sm"
        title={SearchAriaLabel}
        variant="tertiary"
        type="button"
      />
    </HeaderStyledSearchButton>
  );
};

export default AudiHeaderSearchButtonAlpha;
